import * as faceapi from "face-api.js";

let modelsLoadedPromise = null;

export async function loadFaceDetectionModels() {
  if (!modelsLoadedPromise) {
    modelsLoadedPromise = (async () => {
      await faceapi.nets.ssdMobilenetv1.load("/models");
      await faceapi.loadFaceLandmarkModel("/models");
    })();
    modelsLoadedPromise.catch(() => {
      modelsLoadedPromise = null;
    });
  }
  return modelsLoadedPromise;
}

export async function detectFaceLandmarks(image) {
  await loadFaceDetectionModels();
  return faceapi.detectAllFaces(image).withFaceLandmarks();
}

export async function drawLandmarks(landmarks, canvas) {
  landmarks.forEach((face) => {
    new faceapi.draw.DrawFaceLandmarks(face.landmarks).draw(canvas);
  });
}

export class LandmarkCanvas {
  constructor(landmarks, canvas) {
    this.activeSet = null;
    this.activePoint = null;
    this.activeMovement = false;
    this.lastMousePosition = null;
    this.landmarks = landmarks;
    this.canvas = canvas;
    this.context = canvas.getContext("2d");
    this.imageData = this.context.getImageData(
      0,
      0,
      this.canvas.width,
      this.canvas.height,
    );

    this.canvas.addEventListener('touchstart', (event) => {
      this.mouseDown(event);

      if (this.activeSet || this.activePoint) {
        event.preventDefault();
      }
    });
    this.canvas.addEventListener('mousedown', (event) => {
      this.mouseDown(event);
    });

    this.canvas.addEventListener('touchmove', (event) => {
      this.mouseMove(event);

      if (this.activeSet || this.activePoint) {
        event.preventDefault();
      }
    });
    document.addEventListener('mousemove', (event) => {
      this.mouseMove(event);
    });

    document.addEventListener('touchend', () => {
      this.mouseUp();
    });
    document.addEventListener('mouseup', () => {
      this.mouseUp();
    });

    const positions = this.landmarks.positions;
    this.chinPositions = positions.slice(0, 17);
    this.leftEyebrowPositions = positions.slice(17, 22);
    this.rightEyebrowPositions = positions.slice(22, 27);
    this.nosePositions = positions.slice(27, 36);
    this.leftEyePositions = positions.slice(36, 42);
    this.rightEyePositons = positions.slice(42, 48);
    this.outerMouthPositions = positions.slice(48, 60);
    this.innerMouthPositions = positions.slice(60, 68);
    this.allSets = [
      this.chinPositions,
      this.leftEyebrowPositions,
      this.rightEyebrowPositions,
      this.nosePositions,
      this.leftEyePositions,
      this.rightEyePositons,
      this.outerMouthPositions,
      this.innerMouthPositions,
    ]

    this.drawLandmarks();
  }

  redrawCanvas() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.putImageData(this.imageData, 0, 0);
    this.drawLandmarks();
  }

  drawLandmarks() {
    this.drawLandmarkSet(this.chinPositions)
    this.drawLandmarkSet(this.leftEyebrowPositions)
    this.drawLandmarkSet(this.rightEyebrowPositions)
    this.drawLandmarkSet(this.nosePositions)
    this.drawLandmarkSet(this.leftEyePositions, true)
    this.drawLandmarkSet(this.rightEyePositons, true)
    this.drawLandmarkSet(this.outerMouthPositions, true)
    this.drawLandmarkSet(this.innerMouthPositions, true)
  }

  drawLandmarkSet(set, circle = false) {
    const active = this.activeSet === set;

    for(let i = 0; i < set.length; i++) {
      if (i > 0) {
        this.drawLine(set[i-1].x, set[i-1].y, set[i].x, set[i].y, active);
      }
      if (circle && i === set.length - 1) {
        this.drawLine(set[i].x, set[i].y, set[0].x, set[0].y, active);
      }
    }

    for(let i = 0; i < set.length; i++) {
      const activePoint = set[i] === this.activePoint;
      this.drawDot(set[i].x, set[i].y, active, activePoint);
    }
  }

  drawDot(x, y, activeSet, activePoint) {
    const alpha = 0.2;

    if (activeSet) {
      this.context.strokeStyle = `rgba(0, 0, 0, ${alpha})`;
      if (activePoint) {
        this.context.fillStyle = `rgba(255, 255, 255, 0.4`;
      } else {
        this.context.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      }
      this.context.lineWidth = 1;
      this.context.beginPath();
      this.context.arc(x, y, 15, 0, 2 * Math.PI);
      this.context.fill();
      this.context.stroke();
    }

    this.context.strokeStyle = "black";
		this.context.beginPath();
		this.context.arc(x, y, 1, 0, 2 * Math.PI);
		this.context.stroke();
  }

  drawLine(x1, y1, x2, y2, active) {
    let color = color = "rgb(0, 100, 200)";
    if (active) {
      color = "rgb(0, 200, 200)";
    }
    this.context.strokeStyle = color;
		this.context.beginPath();
		this.context.moveTo(x1, y1);
		this.context.lineWidth = 2;
		this.context.lineTo(x2, y2);
		this.context.stroke();
  }

  findClosestDotOnSet(x, y, set) {
    let closestDistance;
    let closestPoint;
    set.forEach((point) => {
      const distanceX = Math.abs(x - point.x);
      const distanceY = Math.abs(y - point.y);
      const distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));

      if (closestDistance === undefined || distance < closestDistance) {
        closestDistance = distance;
        closestPoint = point;
      }
    });

    return [closestDistance, closestPoint];
  }

  chooseLandmarkSet(x, y) {
    let closestSet;
    let closestDistance;
    this.allSets.forEach((set) => {
      const [distance, ] = this.findClosestDotOnSet(x, y, set);

      if (closestDistance === undefined || distance < closestDistance) {
        closestSet = set;
        closestDistance = distance;
      }
    });

    if (closestDistance < 30) {
      this.activeSet = closestSet;
    } else {
      this.activeSet = null;
    }
  }

  chosePoint(x, y) {
    if (this.activeSet !== null) {
      const [distance, closestPoint] = this.findClosestDotOnSet(x, y, this.activeSet);

      if (distance < 15) {
        this.activePoint = closestPoint;
      } else {
        this.activePoint = null;
      }
    }
  }

  getMouseTouchXY(event) {
    const rect = this.canvas.getBoundingClientRect()

    if (event.touches) {
      const x = ((event.touches[0].clientX - rect.left) / rect.width) * this.canvas.width;
      const y = ((event.touches[0].clientY - rect.top) / rect.height) * this.canvas.height;

      return [x, y];
    } else {
      const x = ((event.clientX - rect.left) / rect.width) * this.canvas.width;
      const y = ((event.clientY - rect.top) / rect.height) * this.canvas.height;

      return [x, y];
    }
  }

  getMouseDistance(xMousePosition, yMousePosition) {
    if (this.lastMousePosition === null) {
      return false;
    }
    const x = xMousePosition - this.lastMousePosition.x;
    const y = yMousePosition - this.lastMousePosition.y;

    return {x, y}
  }

  moveActiveSet(xDistance, yDistance) {
    let positionError = false;
    this.activeSet.forEach((point) => {
      if (!positionError) {
        positionError = this.checkPositionError(
          point,
          point.x + xDistance,
          point.y + yDistance,
        );
      }
    });

    if (!positionError) {
      this.activeSet.forEach((point) => {
        this.setPointPosition(
          point,
          point.x + xDistance,
          point.y + yDistance,
        );
      });
    }
  }

  moveActivePoint(xDistance, yDistance) {
    this.setPointPosition(
      this.activePoint,
      this.activePoint.x + xDistance,
      this.activePoint.y + yDistance,
    );
  }

  checkPositionError(point, x, y) {
    let positionError = true;

    if (point) {
      positionError = false;

      if (x < 0) {
        positionError = true;
      } else if (x >= this.canvas.width) {
        positionError = true;
      }

      if (y < 0) {
        positionError = true;
      } else if (y >= this.canvas.height) {
        positionError = true;
      }
    }

    return positionError;
  }

  setPointPosition(point, x, y) {
    if (point && !this.checkPositionError(point, x, y)) {
      point.x = x;
      point.y = y;
    }
  }

  mouseDown(event) {
    const [x, y] = this.getMouseTouchXY(event);
    this.activeMovement = true;

    if (this.activeSet !== null) {
      this.chosePoint(x, y);
    }

    if (this.activePoint === null) {
      this.chooseLandmarkSet(x, y);
      this.redrawCanvas();
    }

    this.lastMousePosition = {x, y};
  }

  mouseMove(event) {
    if (this.activeMovement) {
      const [x, y] = this.getMouseTouchXY(event);
      const distance = this.getMouseDistance(x, y);

      if (distance) {
        if (this.activePoint !== null) {
          this.moveActivePoint(distance.x, distance.y);
          this.redrawCanvas();
        } else if (this.activeSet !== null) {
          this.moveActiveSet(distance.x, distance.y);
          this.redrawCanvas();
        }
      }

      this.lastMousePosition = {x, y};
    }
  }

  mouseUp() {
    if (this.activeMovement || this.activePoint) {
      this.activeMovement = false;
      this.activePoint = null;
      this.lastMousePosition = null;
      this.redrawCanvas();
    }
  }
}
